import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Check } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Conversation script ────────────────────────────────────────
   Each step asks one question and stores the answer under `key`.
   `validate(value)` -> true | error string
─────────────────────────────────────────────────────────────── */
const STEPS = [
  {
    key: "name",
    bot: "Hey there 👋 I'm Aria, your AI assistant from AIFlow Labs. To get you a personalized automation plan, mind sharing your name?",
    placeholder: "Your full name",
    validate: (v) => (v.trim().length >= 2 ? true : "Please share your name (min 2 characters)."),
  },
  {
    key: "phone",
    bot: (name) => `Nice to meet you, ${name.split(" ")[0]}! What's the best phone number to reach you on?`,
    placeholder: "+91 98765 43210",
    validate: (v) => (/^[+\d][\d\s\-()]{5,18}$/.test(v.trim()) ? true : "Please enter a valid phone number."),
  },
  {
    key: "business",
    bot: "Got it. What business are you running?",
    placeholder: "e.g. D2C skincare brand · SaaS startup · Real estate",
    validate: (v) => (v.trim().length >= 2 ? true : "Tell me a bit about your business."),
  },
  {
    key: "problem",
    bot: "Perfect. Which repetitive task is eating up most of your team's time right now?",
    placeholder: "e.g. Responding to WhatsApp leads, processing invoices, follow-ups…",
    validate: (v) => (v.trim().length >= 5 ? true : "A short description (5+ characters) helps me scope this right."),
  },
  {
    key: "budget",
    bot: "Last one. What's your rough monthly budget for an automation system?",
    options: ["Under ₹25k", "₹25k – ₹75k", "₹75k – ₹2L", "₹2L+", "Not sure yet"],
    validate: () => true,
  },
];

const initialState = { idx: 0, answers: {}, messages: [], typing: false, done: false, submitting: false, error: null };

function botText(step, answers) {
  return typeof step.bot === "function" ? step.bot(answers.name || "") : step.bot;
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(initialState);
  const [draft, setDraft] = useState("");
  const [validationError, setValidationError] = useState(null);
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);

  /* Seed the first bot message when chat opens */
  useEffect(() => {
    if (open && state.messages.length === 0) {
      pushBotMessage(botText(STEPS[0], {}));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Auto-scroll on new message */
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [state.messages, state.typing]);

  /* Focus input after each bot message */
  useEffect(() => {
    if (!state.typing && !state.done && inputRef.current) inputRef.current.focus();
  }, [state.typing, state.idx, state.done]);

  function pushBotMessage(text) {
    setState((s) => ({ ...s, typing: true }));
    setTimeout(() => {
      setState((s) => ({
        ...s,
        typing: false,
        messages: [...s.messages, { from: "bot", text }],
      }));
    }, 700);
  }

  async function submitLead(answers) {
    setState((s) => ({ ...s, submitting: true, error: null }));
    let saved = true;
    try {
      await axios.post(`${API}/leads`, answers, { timeout: 8000 });
    } catch (err) {
      saved = false;
      // Backend may not be deployed — gracefully degrade so the user still flows to WhatsApp
      // eslint-disable-next-line no-console
      console.warn("Lead save failed; user will continue via WhatsApp.", err?.message);
    }
    setState((s) => ({ ...s, submitting: false, done: true }));
    const firstName = answers.name.split(" ")[0];
    const tail = saved
      ? "I've shared your details with the team — we'll personally reach out on WhatsApp within 24 hours with a tailored automation plan for your business."
      : "Tap the green button below to continue this chat on WhatsApp — your details are pre-filled so we can pick up exactly where you left off.";
    pushBotMessage(`Thanks ${firstName} 🙌 ${tail}`);
  }

  function handleAnswer(rawValue) {
    const step = STEPS[state.idx];
    const value = rawValue.trim();
    const result = step.validate(value);
    if (result !== true) {
      setValidationError(result);
      return;
    }
    setValidationError(null);
    const nextAnswers = { ...state.answers, [step.key]: value };
    const userMessages = [...state.messages, { from: "user", text: value }];
    const nextIdx = state.idx + 1;
    setDraft("");
    setState((s) => ({ ...s, answers: nextAnswers, messages: userMessages, idx: nextIdx }));

    if (nextIdx < STEPS.length) {
      pushBotMessage(botText(STEPS[nextIdx], nextAnswers));
    } else {
      submitLead(nextAnswers);
    }
  }

  function reset() {
    setState(initialState);
    setDraft("");
    setValidationError(null);
  }

  const currentStep = STEPS[state.idx];
  const showOptions = currentStep?.options && !state.typing && !state.done;
  const showTextInput = currentStep && !currentStep.options && !state.typing && !state.done && !state.submitting;

  return (
    <>
      {/* Floating bubble */}
      <motion.button
        data-testid="chatbot-bubble"
        onClick={() => setOpen((v) => !v)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 320, damping: 22 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-6 right-6 z-[998] w-14 h-14 rounded-full flex items-center justify-center shadow-[0_8px_32px_rgba(99,102,241,0.5)] hover:shadow-[0_12px_40px_rgba(99,102,241,0.7)] transition-shadow"
        style={{ background: "linear-gradient(135deg,#6366f1 0%,#8b5cf6 100%)" }}
      >
        <span className="absolute inset-0 rounded-full bg-indigo-500/40 animate-ping" style={{ animationDuration: "2.4s" }} />
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <X className="w-6 h-6 text-white relative" strokeWidth={2.5} />
            </motion.span>
          ) : (
            <motion.span key="msg" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <MessageCircle className="w-6 h-6 text-white relative" strokeWidth={2.2} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Chat window */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chatbot-window"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="fixed bottom-24 right-6 z-[998] w-[360px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[calc(100vh-7rem)] rounded-2xl overflow-hidden flex flex-col border border-white/[0.07] shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
            style={{ background: "#0a0a0f" }}
          >
            {/* Header */}
            <div className="px-4 py-3.5 flex items-center gap-3 border-b border-white/[0.05]" style={{ background: "linear-gradient(135deg,rgba(99,102,241,0.18),rgba(139,92,246,0.08))" }}>
              <div className="relative w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)" }}>
                <span className="text-white text-[14px] font-bold">A</span>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#0a0a0f]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-semibold text-white tracking-[-0.01em]">Aria · AIFlow Labs</p>
                <p className="text-[11px] text-emerald-400/90 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Online · replies instantly
                </p>
              </div>
              {state.done && (
                <button onClick={reset} data-testid="chatbot-restart" className="text-[11px] font-medium text-white/45 hover:text-white/80 px-2.5 py-1 rounded-md border border-white/10 hover:border-white/20 transition-colors">
                  Restart
                </button>
              )}
            </div>

            {/* Messages */}
            <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chatbot-messages">
              {state.messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className={`flex ${m.from === "bot" ? "justify-start" : "justify-end"}`}
                >
                  <div
                    className={`max-w-[82%] text-[13px] leading-[1.55] tracking-[-0.005em] px-3.5 py-2.5 rounded-2xl ${
                      m.from === "bot"
                        ? "bg-[#1a1a22] text-white/85 rounded-bl-md border border-white/[0.04]"
                        : "text-white rounded-br-md"
                    }`}
                    style={
                      m.from === "user"
                        ? { background: "linear-gradient(135deg,#6366f1,#7c3aed)" }
                        : undefined
                    }
                  >
                    {m.text}
                  </div>
                </motion.div>
              ))}

              {state.typing && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                  <div className="bg-[#1a1a22] border border-white/[0.04] px-4 py-3 rounded-2xl rounded-bl-md flex gap-1">
                    {[0, 1, 2].map((d) => (
                      <span key={d} className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: `${d * 0.15}s`, animationDuration: "1s" }} />
                    ))}
                  </div>
                </motion.div>
              )}

              {state.submitting && (
                <p className="text-[11px] text-white/40 text-center pt-1">Saving your details…</p>
              )}

              {state.done && (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, type: "spring" }} className="flex justify-center pt-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-[11.5px] font-medium">
                    <Check className="w-3.5 h-3.5" />
                    Details captured successfully
                  </div>
                </motion.div>
              )}

              {state.error && (
                <p className="text-[11.5px] text-red-400/90 text-center pt-1" data-testid="chatbot-error">{state.error}</p>
              )}
            </div>

            {/* Input area */}
            <div className="border-t border-white/[0.05] p-3 bg-[#0d0d13]">
              {validationError && (
                <p className="text-[11px] text-red-400/90 mb-1.5 px-1" data-testid="chatbot-validation">{validationError}</p>
              )}

              {showOptions && (
                <div className="flex flex-wrap gap-2" data-testid="chatbot-options">
                  {currentStep.options.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => handleAnswer(opt)}
                      className="text-[12px] px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.1] hover:border-white/20 text-white/80 hover:text-white transition-all"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}

              {showTextInput && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (draft.trim()) handleAnswer(draft);
                  }}
                  className="flex items-center gap-2"
                >
                  <input
                    ref={inputRef}
                    data-testid="chatbot-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={currentStep.placeholder}
                    className="flex-1 bg-[#1a1a22] border border-white/[0.06] focus:border-indigo-500/60 outline-none text-[13px] text-white placeholder:text-white/25 px-3.5 py-2.5 rounded-xl transition-colors"
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    data-testid="chatbot-send"
                    disabled={!draft.trim()}
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
                    style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)" }}
                    aria-label="Send"
                  >
                    <Send className="w-4 h-4" strokeWidth={2.2} />
                  </button>
                </form>
              )}

              {state.done && (
                <a
                  href={`https://wa.me/918248195127?text=${encodeURIComponent(
                    `Hi AIFlow Labs! I just filled the chatbot:\n\n` +
                    `• Name: ${state.answers.name || ""}\n` +
                    `• Phone: ${state.answers.phone || ""}\n` +
                    `• Business: ${state.answers.business || ""}\n` +
                    `• Repetitive problem: ${state.answers.problem || ""}\n` +
                    `• Monthly budget: ${state.answers.budget || ""}\n\n` +
                    `Looking forward to your tailored automation plan.`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="chatbot-whatsapp-cta"
                  className="block text-center text-[12.5px] font-semibold px-4 py-2.5 rounded-xl text-white transition-opacity hover:opacity-90"
                  style={{ background: "linear-gradient(135deg,#10b981,#059669)" }}
                >
                  Continue on WhatsApp
                </a>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
